<template>
  <div class="i-start">
    <iBackground :color="white" />

    <template v-if="reportStore.invalidData">
      <iUnavailableCard
        text="Please try again later. If the issue persists please contact us at support@jobtest.org"
        title="Report unavailable"
      />
    </template>

    <template v-else>
      <template v-if="data.lightBoxVideo">
        <iBaseLightBox @close="data.lightBoxVideo = ''">
          <video
            :src="data.lightBoxVideo"
            autoplay
            controls
            class="light-box-video"
            type="video/mp4"
          ></video>
        </iBaseLightBox>
      </template>

      <div class="page">
        <iAnnouncementBar version="light" />

        <iReportCountdown
          ref="reportCountdown"
          version="begin"
          has-fade-in-animation
          :timer-duration-min="data.timerDurationMin"
          @scroll-to-payment-table="scrollToPaymentTable"
        />

        <div class="content">
          <iHeroSection
            version="begin"
            @scroll-to-three-keys="scrollToThreeKeys"
          />
          <iThreeKeys />
          <iCoachSchoolDisplay />

          <section
            ref="believeSection"
            class="we-believe"
          >
            <div class="header">Find Your Path. Unlock Your Wealth.</div>
            <div class="earning-potential"><iEarningPotential /></div>
          </section>

          <section class="ai-coach">
            <iLiveCoach @scroll-to-payment-table="scrollToPaymentTable" />
          </section>

          <section
            ref="transformSection"
            class="we-transform"
          >
            <div class="padding">
              <div class="header">Turn Mondays into Your Favorite Day.</div>
              <div class="subheading is-we-transform">
                Your dream job is not a myth. Join thousands of others who found their calling where you can't wait to
                start each week.
              </div>
            </div>

            <div class="slider-testimonials">
              <template v-if="isSmallOrLarger">
                <iBaseAnimatedSlider
                  sliding-direction="left"
                  animation-duration-secs="60"
                >
                  <iSliderTestimonials
                    @clicked="(testimonialVideo: string) => (data.lightBoxVideo = testimonialVideo)"
                  />
                </iBaseAnimatedSlider>
              </template>
              <template v-else> <iSliderTestimonialsMobile /></template>
            </div>

            <iBaseAnimatedSlider
              sliding-direction="right"
              animation-duration-secs="210"
            >
              <iSliderReviews :set="1" />
            </iBaseAnimatedSlider>

            <iBaseAnimatedSlider
              sliding-direction="right"
              animation-duration-secs="140"
            >
              <iSliderReviews :set="2" />
            </iBaseAnimatedSlider>
          </section>

          <section
            id="payment-section"
            class="we-recommend"
          >
            <div class="payment-table">
              <iPaymentTable
                :resume-builder-claimed="data.resumeBuilderClaimed"
                :minutes-left="formattedResumeTimeRemaining.minutes"
                :seconds-left="formattedResumeTimeRemaining.seconds"
                @claim-resume-builder="embedClaimResumeBuilder"
              />
            </div>
          </section>

          <section class="we-guarantee">
            <iMoneyBackGuarantee />
          </section>
        </div>

        <iFooter />
      </div>

      <div class="sale-footer">
        <span>{{ new Date().getFullYear() }} Sale</span>
        <span class="divider">|</span>
        <span>20% OFF</span>
        <span class="divider">|</span>
        <span
          :class="{ clickable: !data.codeApplied, underlined: !data.codeApplied }"
          @click="handleGetCode"
        >
          {{ data.codeApplied ? "Automatically Applied" : "Get Code" }}
        </span>
      </div>
    </template>

    <iResumeBuilderModal
      ref="resumeBuilderModal"
      :visible="data.showResumeBuilderModal"
      :minutes-left="formattedResumeTimeRemaining.minutes"
      :seconds-left="formattedResumeTimeRemaining.seconds"
      @close="data.showResumeBuilderModal = false"
      @claim-resume-builder="modalClaimResumeBuilder"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, type Ref } from "vue"
import { useReportStore } from "@/stores/report"

import useIsScreenSize from "@/composables/useIsScreenSize"
import api from "@/services/apiClient"

// STYLES
import { white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

// COMPONENTS
import iBaseAnimatedSlider from "@/components/base/BaseAnimatedSlider.vue"
import iBaseLightBox from "@/components/base/BaseLightBox.vue"
import iBackground from "@/components/global/Background.vue"
import iSliderReviews from "@/components/global/SliderReviews.vue"
import iSliderTestimonials from "@/components/global/SliderTestimonials.vue"
import iSliderTestimonialsMobile from "@/components/global/SliderTestimonialsMobile.vue"
import iUnavailableCard from "@/components/global/UnavailableCard.vue"
import iPaymentTable from "@/components/pricing/PaymentTableTest5.vue"
import iAnnouncementBar from "@/components/welcome/AnnouncementBar.vue"
import iEarningPotential from "@/components/welcome/EarningPotential.vue"
import iResumeBuilderModal from "@/components/welcome/ResumeBuilderModal1.vue"
import iLiveCoach from "@/components/getStarted/LiveCoach.vue"
import iFooter from "@/components/welcome/Footer.vue"
import iMoneyBackGuarantee from "@/components/welcome/MoneyBackGuarantee.vue"
import iReportCountdown from "@/components/welcome/ReportCountdown.vue"
import iHeroSection from "@/components/welcome/HeroSection.vue"
import iThreeKeys from "@/components/welcome/ThreeKeys.vue"
import iCoachSchoolDisplay from "@/components/welcome/CoachSchoolDisplay.vue"

// STORES
const reportStore = useReportStore()

// REFS
const reportCountdown: Ref<HTMLElement | null> = ref(null)
const transformSection: Ref<HTMLElement | null> = ref(null)
const believeSection: Ref<HTMLElement | null> = ref(null)
const resumeBuilderModal: Ref<HTMLElement | null> = ref(null)
const resumeTimeRemaining = ref(600) // in seconds

// DATA
const isSmallOrLarger = useIsScreenSize(breakpointSm)

const data = reactive({
  lightBoxVideo: "",
  timerDurationMin: 15,
  transformSectionDelayMs: 2000,
  believeSectionDelayMs: 1500,
  reportCountdownOffset: "0px",
  codeApplied: false,
  showResumeBuilderModal: false,
  resumeBuilderClaimed: null as "modal" | "embed" | null,
  resumeTimerInterval: undefined as undefined | ReturnType<typeof setInterval>
})

// COMPUTED
const formattedResumeTimeRemaining = computed(() => {
  const minutes = Math.floor((resumeTimeRemaining.value / 60) % 60)
  const seconds = Math.floor(resumeTimeRemaining.value % 60)

  return { minutes: `${minutes}`, seconds: `${seconds}` }
})

// FUNCTIONS
function scrollTo(element: Element | null) {
  if (!element) return

  // Calculate the target scroll position and scroll to
  const targetScrollPosition = element.getBoundingClientRect().top + window.scrollY - 16
  window.scrollTo({ top: targetScrollPosition, behavior: "smooth" })
}

function scrollToPaymentTable() {
  const paymentSection = document.querySelector("#payment-section")
  scrollTo(paymentSection)
}

function scrollToThreeKeys() {
  const threeKeysSection = document.querySelector(".i-three-keys")
  scrollTo(threeKeysSection)
}

function handleGetCode() {
  if (!data.codeApplied) {
    scrollToPaymentTable()
    data.codeApplied = true
  }
}

function observeTransformSection(delayMs: number) {
  setTimeout(() => {
    if (!transformSection.value || !reportCountdown.value) return
    setupObserver(transformSection, () => {
      ;(reportCountdown.value as any)?.makeActive?.()
    })
  }, delayMs)
}

function observeBelieveSection(delayMs: number) {
  setTimeout(() => {
    if (!believeSection.value || !resumeBuilderModal.value) return
    setupObserver(believeSection, () => {
      data.showResumeBuilderModal = true
      startTimer()
    })
  }, delayMs)
}

function setupObserver(elementRef: Ref<HTMLElement | null>, callback: () => void) {
  if (!elementRef.value) return
  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback() // Execute the callback function
          if (elementRef.value === transformSection.value) onReportCountdownResize()
          observer.unobserve(entry.target) // Stop observing after the first trigger
        }
      })
    },
    {
      root: null, // observing changes in the viewport
      rootMargin: "0px",
      threshold: elementRef.value === transformSection.value ? 0.1 : 0.75 // callback will be executed when 10% of the target is visible for 'transform' and 75% for 'believe'
    }
  )
  observer.observe(elementRef.value)
}

function onReportCountdownResize() {
  nextTick(() => {
    const reportCountdown = document.querySelector(".i-report-countdown") as HTMLElement

    if (!reportCountdown) return

    const observer = new ResizeObserver(() => {
      const offsetHeight = `${reportCountdown.getBoundingClientRect().height}px`
      if (data.reportCountdownOffset != offsetHeight) data.reportCountdownOffset = offsetHeight
    })
    observer.observe(reportCountdown)
  })
}

const modalClaimResumeBuilder = async () => {
  data.showResumeBuilderModal = false
  data.resumeBuilderClaimed = "modal"
  scrollToPaymentTable()

  try {
    await api.event.postEvent("resumeBuilderGiftClaimedModal")
  } catch (error) {
    console.error("Error recording resumeBuilderGiftClaimedModal event:", error)
  }
}

const embedClaimResumeBuilder = async () => {
  data.resumeBuilderClaimed = "embed"

  try {
    await api.event.postEvent("resumeBuilderGiftClaimedEmbed")
  } catch (error) {
    console.error("Error recording resumeBuilderGiftClaimedEmbed event:", error)
  }
}

const startTimer = () => {
  clearInterval(data.resumeTimerInterval)

  data.resumeTimerInterval = setInterval(() => {
    if (resumeTimeRemaining.value > 0) resumeTimeRemaining.value--

    if (resumeTimeRemaining.value <= 0) {
      clearInterval(data.resumeTimerInterval)
    }
  }, 1000)
}

// LIFECYCLE HOOKS
onMounted(() => {
  observeBelieveSection(data.believeSectionDelayMs)
  observeTransformSection(data.transformSectionDelayMs)
})
</script>

<style scoped lang="scss">
.i-start {
  .zigpoll-modal {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }

    &-wrap {
      display: flex;
      flex-direction: column;
      min-height: 90px;
      max-height: 60vh;
      overflow: auto;
      position: relative;
    }

    &-text {
      font-weight: bold;
      position: absolute;
      text-align: center;
      top: 50%;
      width: 100%;
    }
  }

  .page {
    overflow-x: clip;

    .content {
      @include flex($direction: column, $justify: start, $align: stretch, $wrap: nowrap, $gap: 0);

      margin: auto;
      width: 100%;
      max-width: 960px;

      @media screen and (min-width: $breakpoint-xl) {
        max-width: 1410px;
      }

      section {
        &.ai-coach {
          margin-top: 5px;
          margin-bottom: 60px;
          padding-left: 16px;
          padding-right: 16px;

          @media screen and (min-width: $breakpoint-sm) {
            padding-left: 40px;
            padding-right: 40px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding-left: 16px;
            padding-right: 16px;
          }
        }

        &.we-believe {
          margin-top: 24px;
          margin-bottom: 80px;
          padding-left: 16px;
          padding-right: 16px;

          @media screen and (min-width: $breakpoint-sm) {
            padding-left: 40px;
            padding-right: 40px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding-left: 16px;
            padding-right: 16px;
          }

          .earning-potential {
            margin-top: 40px;
            padding-left: 16px;
            padding-right: 16px;
            padding-top: 48px;
            border: 2px solid $black100;
            border-radius: 24px;
            overflow: hidden;

            @media screen and (min-width: $breakpoint-sm) {
              padding-left: 40px;
              padding-right: 40px;
            }

            @media screen and (min-width: $breakpoint-md) {
              margin-top: 50px;
              padding-top: 80px;
            }

            @media screen and (min-width: $breakpoint-lg) {
              padding-top: 0;
            }

            @media screen and (min-width: $breakpoint-xl) {
              padding-left: 16px;
              padding-right: 16px;
              max-width: unset;
            }
          }
        }

        &.we-transform {
          margin-top: 24px;
          margin-bottom: 80px;

          .padding {
            padding-left: 16px;
            padding-right: 16px;

            @media screen and (min-width: $breakpoint-sm) {
              padding-left: 40px;
              padding-right: 40px;
            }

            @media screen and (min-width: $breakpoint-lg) {
              padding-left: 16px;
              padding-right: 16px;
            }
          }

          .slider-testimonials {
            margin-top: 32px;
            margin-bottom: 0;

            @media screen and (min-width: $breakpoint-md) {
              margin-top: 60px;
              margin-bottom: 24px;
            }
          }
        }

        &.we-recommend {
          margin-left: auto;
          margin-right: auto;
          padding: 20px 16px;
          width: 100%;

          @media screen and (min-width: $breakpoint-sm) {
            padding-left: 40px;
            padding-right: 40px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding: 20px 0;
          }

          @media screen and (min-width: $breakpoint-xl) {
            padding-left: 16px;
            padding-right: 16px;
            max-width: unset;
          }

          .payment-table {
            margin-left: auto;
            margin-right: auto;
          }
        }

        &.we-guarantee {
          max-width: 800px;
          margin: 80px auto;
          width: 100%;
          padding-left: 16px;
          padding-right: 16px;

          @media screen and (min-width: $breakpoint-sm) {
            padding-left: 40px;
            padding-right: 40px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      }

      .header {
        @include display-sm;
        @include flex(column, start, center);

        font-weight: 700;
        text-align: center;

        @media screen and (min-width: $breakpoint-sm) {
          font-size: 40px;
          line-height: 50px;
        }
      }

      .subheading {
        @include text-md;

        font-weight: 500;
        margin: 15px auto auto;
        text-align: center;
        max-width: 520px;

        &.is-we-believe {
          max-width: 450px;
        }
      }
    }
  }

  .modal {
    .bottom {
      @include flex($direction: row, $justify: center, $align: center, $gap: 0);

      background-color: $black50;
      padding: 12px 25px;
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;
    }
  }

  .sale-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-image: $blue-purple-gradient;
    color: #fff;
    text-align: center;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 500;
    z-index: 800;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;

    .divider {
      opacity: 0.5;
      font-weight: 300;
    }

    .clickable {
      cursor: pointer;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.8;
      }
    }

    .underlined {
      text-decoration: underline;
    }

    @media screen and (min-width: $breakpoint-xs) {
      font-size: 12px;
      gap: 12px;
    }

    @media screen and (min-width: $breakpoint-sm) {
      font-size: 14px;
      gap: 16px;
    }

    @media screen and (min-width: $breakpoint-md) {
      font-size: 16px;
      gap: 20px;
    }
  }
}
</style>
